import React, { useState, useEffect, useContext } from "react";
import "../../profileAdminCard/adminModals.css";
import "./staffInfoModal.css";
import { CombinedContext } from "../../../context/combinedContext";
import companyBasedRequest from "../../../request/companyBasedRequest";
import translations from "../../../translations/translations";
import PhoneInput from "../../common/phoneInput/phoneInput";
import useModalDismiss from "../../common/useModalDismiss";

const BIO_MAX = 300;

const StaffInfoModal = ({ open, onClose, staffProfile, onSave }) => {
	useModalDismiss(onClose, { enabled: open });
	const { userData, language } = useContext(CombinedContext);

	const [local, setLocal] = useState({
		firstName: "",
		lastName: "",
		bio: "",
		phone: "",
	});

	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (staffProfile) {
			setLocal({
				firstName: staffProfile.firstName || "",
				lastName: staffProfile.lastName || "",
				bio: staffProfile.bio || "",
				// was missing, so the phone field rendered empty and its value was
				// dropped on save — see the API payload below
				phone: staffProfile.phone || "",
			});
		}
	}, [staffProfile]);

	if (!open) return null;

	const t = translations[language].teamPage;

	const handleSave = async () => {
		// The endpoint takes `phoneNumber`; the card state keeps it as `phone`.
		// Same split profileStaffCard/staffInfoModal uses against this route.
		const apiData = {
			firstName: local.firstName,
			lastName: local.lastName,
			bio: local.bio,
			phoneNumber: local.phone,
		};

		try {
			setSaving(true);

			const res = await companyBasedRequest.updateStaffInfo(
				staffProfile.staffId,
				apiData,
				userData.cognitoTokens.AccessToken
			);

			/* Patch the page off the user the route saved rather than off the
			   inputs, so the roster and detail panel show exactly what stuck.
			   `fullName` is derived here because the route doesn't return it and
			   the profile heading renders it — that gap is what left the name
			   stale until a reload. Both phone keys are carried, since staff
			   objects read `phone` and the context user reads `phoneNumber`. */
			const saved = res?.user || {};
			const firstName = saved.firstName ?? local.firstName;
			const lastName = saved.lastName ?? local.lastName;
			const phone = saved.phone ?? saved.phoneNumber ?? local.phone;

			onSave?.({
				firstName,
				lastName,
				fullName: [firstName, lastName].filter(Boolean).join(" "),
				bio: saved.bio ?? local.bio,
				phone,
				phoneNumber: phone,
			});

			onClose(); // close only after success
		} catch (err) {
			console.error("❌ Failed updating staff info", err);
		} finally {
			setSaving(false);
		}
	};

	const bioCount = local.bio.length;

	return (
		<div className="sf-pm-overlay">
			<div className="sf-pm-modal is-compact">
				{/* HEADER */}
				<div className="sf-pm-header">
					<div className="sf-pm-heading">
						<h2 className="sf-pm-title">{t.editProfile}</h2>
						<p className="sf-pm-sub">{t.modal.info.description}</p>
					</div>
					<button className="sf-pm-close" onClick={onClose} aria-label={t.actions.cancel}>
						✕
					</button>
				</div>

				{/* BODY */}
				<div className="sf-pm-body">
					<section className="sf-pm-section">
						<p className="sf-pm-label">{t.main.profile}</p>

						<div className="sf-pm-card">
							<div className="sf-pm-grid-2">
								<div className="sf-pm-field">
									<label className="sf-pm-field-label" htmlFor="sf-pm-staff-first">
										{t.labels.firstName}
									</label>
									<input
										id="sf-pm-staff-first"
										className="sf-pm-input"
										value={local.firstName}
										onChange={(e) => setLocal({ ...local, firstName: e.target.value })}
									/>
								</div>

								<div className="sf-pm-field">
									<label className="sf-pm-field-label" htmlFor="sf-pm-staff-last">
										{t.labels.lastName}
									</label>
									<input
										id="sf-pm-staff-last"
										className="sf-pm-input"
										value={local.lastName}
										onChange={(e) => setLocal({ ...local, lastName: e.target.value })}
									/>
								</div>
							</div>

							<div className="sf-pm-field">
								<label className="sf-pm-field-label">{t.labels.phoneShort}</label>
								<PhoneInput
									businessPhone={local.phone}
									setBusinessPhone={(value) => setLocal((prev) => ({ ...prev, phone: value }))}
								/>
							</div>

							<div className="sf-pm-field">
								<label className="sf-pm-field-label" htmlFor="sf-pm-staff-bio">
									{t.labels.bio}
								</label>
								<div className="sf-pm-counted">
									<textarea
										id="sf-pm-staff-bio"
										className="sf-pm-textarea"
										value={local.bio}
										maxLength={BIO_MAX}
										onChange={(e) => setLocal({ ...local, bio: e.target.value })}
									/>
									<span className={`sf-pm-counter${bioCount >= BIO_MAX - 30 ? " is-warn" : ""}`}>
										{bioCount}/{BIO_MAX}
									</span>
								</div>
							</div>
						</div>
					</section>
				</div>

				{/* FOOTER */}
				<div className="sf-pm-footer">
					<button className="sf-pm-btn is-ghost" onClick={onClose} disabled={saving}>
						{translations[language].calendar.editor.cancel}
					</button>

					<button className="sf-pm-btn is-primary" onClick={handleSave} disabled={saving}>
						{saving && <span className="sf-pm-spinner" aria-hidden="true" />}
						{saving
							? translations[language].calendar.editor.saving
							: translations[language].calendar.editor.save}
					</button>
				</div>
			</div>
		</div>
	);
};

export default StaffInfoModal;
